import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../api';
import LoadingState from '../components/LoadingState';
import ErrorState from '../components/ErrorState';
import { markdownToHtml } from '../utils/markdownToHtml';
import { sanitizeHtml } from '../utils/sanitizeHtml';
import { logger } from '../utils/logger';
import { setMetaTags, resetMetaTags, buildCanonicalUrl, buildArticleJsonLd } from '../utils/seo';

interface ArticleDetail {
  id: string;
  title: string;
  slug: string;
  content: string;
  published_at: string;
  author: string;
  tags: string[];
  cover_image?: string;
}

interface ArticleResponse {
  success: boolean;
  data: ArticleDetail;
}

function resolveCoverSrc(coverImage: string | undefined, slug: string): string {
  if (!coverImage) return `/images/articles/${slug}.svg`;
  if (coverImage.trimStart().startsWith('<svg')) {
    return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(coverImage)}`;
  }
  return coverImage;
}

const ArticleDetailPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [article, setArticle] = useState<ArticleDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!slug) {
      setError('Статья не найдена');
      setLoading(false);
      return;
    }

    const fetchArticle = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await api.get<ArticleResponse>(`/articles/${slug}`);
        logger.debug('[ArticleDetail] Response:', JSON.stringify({
          success: res.success,
          hasData: !!res.data,
          dataType: typeof res.data
        }));

        if (res.success && res.data) {
          const serverResponse = res.data as any;
          const articleData = serverResponse.data || serverResponse;
          logger.debug('[ArticleDetail] Article:', articleData?.title || 'no title');
          setArticle(articleData);

          // Обновляем SEO meta-теги
          if (articleData) {
            const excerpt = articleData.excerpt || articleData.content?.substring(0, 200) || '';
            const articleSlug = articleData.slug || slug;
            setMetaTags({
              title: articleData.title || articleData.meta_title || 'Статья',
              description: articleData.meta_description || excerpt.substring(0, 160),
              canonicalUrl: buildCanonicalUrl(`/articles/${articleSlug}`),
              ogType: 'article',
              keywords: articleData.meta_keywords || articleData.tags?.join(', ') || '',
              jsonLd: buildArticleJsonLd({
                title: articleData.title || 'Статья',
                description: articleData.meta_description || excerpt.substring(0, 160),
                slug: articleSlug,
                publishedAt: articleData.published_at || articleData.created_at,
                author: articleData.author_name || 'Astranum',
                keywords: articleData.meta_keywords,
              }),
            });
          }
        } else {
          setError(res.error || 'Не удалось загрузить статью');
        }
      } catch {
        setError('Ошибка соединения с сервером');
      } finally {
        setLoading(false);
      }
    };

    fetchArticle();

    return () => {
      resetMetaTags();
    };
  }, [slug]);

  const formatDate = (dateStr: string): string => {
    try {
      return new Date(dateStr).toLocaleDateString('ru-RU', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      });
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '80vh',
        padding: '20px 20px',
        maxWidth: '800px',
        margin: '0 auto',
      }}>
        <LoadingState text="Загружаем статью..." />
      </div>
    );
  }

  if (error || !article) {
    return (
      <div style={{
        minHeight: '80vh',
        padding: '20px 20px',
        maxWidth: '800px',
        margin: '0 auto',
      }}>
        <ErrorState
          message={error || 'Статья не найдена'}
          onRetry={() => navigate('/articles')}
        />
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '80vh',
      padding: isMobile ? '20px 12px' : '32px 20px',
      maxWidth: '800px',
      margin: '0 auto',
      position: 'relative',
    }}>
      <div style={{ position: 'absolute', top: 0, left: '10%', right: '10%', height: '2px', background: 'linear-gradient(90deg, transparent, #d4af37, transparent)' }} />
      {/* Хлебные крошки */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '32px',
        fontSize: '14px',
        fontFamily: 'var(--font-sans)',
      }}>
        <button
          onClick={() => navigate('/articles')}
          style={{
            background: 'none',
            border: 'none',
            color: '#d4af37',
            cursor: 'pointer',
            fontSize: '14px',
            padding: 0,
            fontFamily: 'var(--font-sans)',
          }}
        >
          Статьи
        </button>
        <span style={{ color: '#666' }}>/</span>
        <span style={{ color: '#888' }}>{article.title}</span>
      </div>

      {/* Картинка статьи */}
      <div style={{
        borderRadius: '24px',
        overflow: 'hidden',
        marginTop: '16px',
        marginBottom: '24px',
        background: 'rgba(13, 20, 40, 0.85)',
        backdropFilter: 'blur(20px)',
        border: '1px solid rgba(212, 175, 55, 0.12)',
        padding: '12px',
        minHeight: '200px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: '0 8px 40px rgba(212, 175, 55, 0.08), inset 0 0 40px rgba(13, 20, 40, 0.5)',
      }}>
        <img
          src={resolveCoverSrc(article.cover_image, article.slug)}
          alt={article.title}
          style={{
            width: '100%',
            height: 'auto',
            minHeight: '180px',
            display: 'block',
            borderRadius: '12px',
            objectFit: 'cover',
          }}
          onError={(e) => {
            e.currentTarget.style.display = 'none';
          }}
        />
      </div>

      {/* Заголовок статьи НАД контентом */}
      <div style={{
        background: 'rgba(13, 20, 40, 0.85)',
        backdropFilter: 'blur(20px)',
        border: '1px solid rgba(212, 175, 55, 0.12)',
        borderRadius: '24px',
        padding: isMobile ? '24px 20px' : '32px 40px',
        marginBottom: '24px',
        boxShadow: '0 8px 40px rgba(212, 175, 55, 0.08), inset 0 0 40px rgba(13, 20, 40, 0.5)',
      }}>
        <h1 style={{
          fontSize: isMobile ? 'clamp(24px, 4vw, 28px)' : 'clamp(28px, 4vw, 36px)',
          fontFamily: 'var(--font-display)',
          fontStyle: 'italic',
          background: 'linear-gradient(135deg, #F4E4A6 0%, #D4AF37 40%, #B8942B 70%, #F4E4A6 100%)',
          backgroundSize: '200% auto',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
          marginBottom: '16px',
          lineHeight: 1.3,
        }}>
          {article.title}
        </h1>

        {/* Мета-информация */}
        <div style={{
          display: 'flex',
          gap: '16px',
          alignItems: 'center',
          color: '#888',
          fontSize: '14px',
          fontFamily: 'var(--font-sans)',
          flexWrap: 'wrap',
        }}>
          <span>{article.author}</span>
          <span style={{ color: '#444' }}>|</span>
          <span>{formatDate(article.published_at)}</span>
        </div>

        {/* Теги */}
        {article.tags && article.tags.length > 0 && (
          <div style={{
            display: 'flex',
            gap: '8px',
            flexWrap: 'wrap',
            marginTop: '16px',
          }}>
            {article.tags.map((tag) => (
              <span
                key={tag}
                style={{
                  display: 'inline-block',
                  background: 'rgba(212, 175, 55, 0.1)',
                  color: '#d4af37',
                  fontSize: '12px',
                  padding: '4px 14px',
                  borderRadius: '12px',
                  fontFamily: 'var(--font-sans)',
                }}
              >
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Контент статьи */}
      <div style={{
        background: 'rgba(13, 20, 40, 0.85)',
        backdropFilter: 'blur(20px)',
        border: '1px solid rgba(212, 175, 55, 0.12)',
        borderRadius: '24px',
        padding: isMobile ? '24px 20px' : '40px',
        marginBottom: '32px',
        boxShadow: '0 8px 40px rgba(212, 175, 55, 0.08), inset 0 0 40px rgba(13, 20, 40, 0.5)',
      }}>
        <div
          className="article-content"
          dangerouslySetInnerHTML={{ __html: sanitizeHtml(markdownToHtml(article.content)) }}
          style={{
            color: '#e0e0e0',
            fontSize: '16px',
            lineHeight: 1.9,
            fontFamily: 'var(--font-sans)',
          }}
        />
        <style>{`
          .article-content h1 {
            font-size: 28px;
            font-family: var(--font-display);
            font-style: italic;
            color: #d4af37;
            margin: 32px 0 16px;
          }
          .article-content h2 {
            font-size: 24px;
            font-family: var(--font-display);
            font-style: italic;
            color: #d4af37;
            margin: 28px 0 14px;
          }
          .article-content h3 {
            font-size: 20px;
            font-family: var(--font-display);
            font-style: italic;
            color: #d4af37;
            margin: 24px 0 12px;
          }
          .article-content strong {
            color: #f4e4a6;
            font-weight: 600;
          }
          .article-content em {
            color: #c0c0c0;
            font-style: italic;
          }
          .article-content blockquote {
            padding-left: 20px;
            margin: 20px 0;
            color: #b0b0b0;
            font-style: italic;
          }
          .article-content ul {
            padding-left: 24px;
            margin: 16px 0;
          }
          .article-content li {
            margin: 8px 0;
          }
          .article-content hr {
            border: none;
            border-top: 1px solid rgba(212, 175, 55, 0.2);
            margin: 32px 0;
          }
          .article-content .tldr {
            background: rgba(212, 175, 55, 0.1);
            padding: 16px 20px;
            margin: 20px 0;
            border-radius: 8px;
          }
          .article-content p {
            margin: 16px 0;
          }
          .article-content br {
            display: none;
          }
          .article-content a {
            color: #7DD3C0;
            text-decoration: underline;
          }
        `}</style>
      </div>

      {/* Навигация */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '16px',
      }}>
        <button
          onClick={() => navigate('/articles')}
          style={{
            background: 'none',
            border: '1px solid rgba(212, 175, 55, 0.4)',
            color: '#d4af37',
            fontSize: '15px',
            padding: '12px 32px',
            borderRadius: '25px',
            cursor: 'pointer',
            fontFamily: 'var(--font-sans)',
            transition: 'all 0.3s',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(212, 175, 55, 0.1)'; e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(212, 175, 55, 0.2)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
        >
          Все статьи
        </button>

        <button
          onClick={() => navigate('/')}
          style={{
            background: 'none',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            color: '#b0b0b0',
            fontSize: '15px',
            padding: '12px 32px',
            borderRadius: '25px',
            cursor: 'pointer',
            fontFamily: 'var(--font-sans)',
            transition: 'all 0.3s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
            e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.4)';
            e.currentTarget.style.transform = 'translateY(-2px)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'none';
            e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
            e.currentTarget.style.transform = 'translateY(0)';
          }}
        >
          На главную
        </button>
      </div>
    </div>
  );
};

export default ArticleDetailPage;
